import { useEffect, useRef } from "react";
import classNames from "classnames";
import { Mixpanel } from "lib/mixpanel/mixpanel";

import type { BriefingReportPageData } from "content-ui/briefings/fetchers/briefingReportFetcher";

import Heading from "content-ui/briefings/components/briefingsReports/elements/heading";
import CardContainer from "content-ui/briefings/components/briefingsReports/elements/cardContainer";
import { MarkdownTracked } from "components/markdownTracked/markdownTracked";
import { MIXPANEL_TOKEN } from "lib/constants";
import { useUserData } from "lib/store/user";

const SuccessCard = ({
  briefing,
  id,
}: {
  briefing: BriefingReportPageData;
  id: string;
}) => {
  const mixpanelRef = useRef<Mixpanel | null>(null);
  const { userId } = useUserData();

  useEffect(() => {
    mixpanelRef.current = new Mixpanel(MIXPANEL_TOKEN);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const StageTag = ({
    first,
    second,
    spaced = true,
  }: {
    first: string;
    second: string;
    spaced?: boolean;
  }) => {
    return (
      <>
        <span>{first}</span>
        <span className="hidden md:inline-block">
          {/* CharCode 160 is a space */}
          {spaced && String.fromCharCode(160)}
        </span>
        <span>{second}</span>
      </>
    );
  };

  const stages = {
    "(Pre-)Seed": <StageTag first={"(Pre-)"} second={"Seed"} spaced={false} />,
    "Series A": <StageTag first={"Series"} second={"A"} />,
    "Series B": <StageTag first={"Series"} second={"B"} />,
    "Series C": <StageTag first={"Series"} second={"C"} />,
    "Series D+": <StageTag first={"Series"} second={"D+"} />,
    "IPO/Exit": <StageTag first={"IPO/"} second={"Exit"} spaced={false} />,
  };

  return (
    <CardContainer id={`content__${id}`}>
      <Heading text={briefing.successStoriesHeading} id={id} />
      <p className="text-[16px] md:-mb-2 md:mt-2 md:text-[24px]">
        Who early stage startups are up against
      </p>

      {briefing.successStories.map((company, i) => (
        <div key={i} className="mt-[16px] md:mt-[32px] md:text-[16px]">
          <div className="flex items-center">
            <p className="mb-[4px] text-[20px] font-[700] leading-none text-cerise md:mb-[10px] md:text-[24px]">
              {company.companyUrl ? (
                <a
                  onClick={() => {
                    if (userId && mixpanelRef.current) {
                      mixpanelRef.current.track("Link Click", {
                        "Link Click": company.companyUrl,
                        Component: "Successes",
                        distinct_id: userId,
                      });
                    }
                  }}
                  target="_blank"
                  rel="noreferrer"
                  href={company.companyUrl}
                >
                  {company.name}
                </a>
              ) : (
                company.name
              )}
            </p>
          </div>

          <div className="mt-[8px] flex items-end gap-[6px] md:items-center md:gap-[12px]">
            {Object.keys(stages).map((stage, i) => {
              const currentStageIndex = Object.keys(stages).indexOf(
                company.stage
              );
              const fillClasses = classNames(
                "mt-[4px] h-[8px] w-[46px] md:w-[60px] rounded-xl self-center",
                {
                  "bg-[#8E8E8E]": i > currentStageIndex,
                  "bg-[#002EA5]": i <= currentStageIndex,
                }
              );

              const textClass = classNames(
                "text-center text-[12px] flex flex-col md:block",
                {
                  "text-[#8E8E8E]": i > currentStageIndex,
                  "text-[#002EA5]": i <= currentStageIndex,
                }
              );

              return (
                <div key={i} className="flex flex-col">
                  <p className={textClass}>{stages[stage]}</p>
                  <span className={fillClasses}></span>
                </div>
              );
            })}
          </div>
          <MarkdownTracked
            text={company.description}
            className="ml-[30px] mt-[16px] list-disc text-[16px] md:ml-[40px] md:text-[21px]"
            component="Successes"
          />
        </div>
      ))}
    </CardContainer>
  );
};

export default SuccessCard;
